import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ParentComparisonStep from "../steps/ParentComparisonStep";
import ChildrenComparisonStep from "../steps/ChildrenComparisonStep";
import ReviewStep from "../steps/ReviewStep";
import RelationsOverviewStep from "../steps/RelationsOverviewStep";
import useParentMergeWizard from "../hooks/useParentMergeWizard";
import {
  mergeParentFields,
  pickMergeableParentFields,
  stripUnwantedParentFields,
} from "../utils/mergeParentFields";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const ParentMergeWizardModal = ({
  show,
  mode = "application-conflict",
  existingParent1,
  newParent1,
  existingParent2 = null,
  newParent2 = null,
  existingChildren = [],
  newChildren = [],
  targetRelations = {},
  sourceRelations = {},
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const [portalElement, setPortalElement] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  const {
    step,
    parent1Choices,
    setParent1Choices,
    parent2Choices,
    setParent2Choices,
    childrenChoices,
    setChildrenChoices,
    goToNextStep,
    goToPrevStep,
  } = useParentMergeWizard({
    show,
    existingParent1,
    newParent1,
    existingParent2,
    newParent2,
    existingChildren,
    newChildren,
  });

  const handleFinalSubmit = async () => {
    const mergedParent1Candidate = mergeParentFields(
      existingParent1,
      newParent1,
      parent1Choices
    );
    const mergedParent1 =
      mode === "manual-parent-merge"
        ? pickMergeableParentFields(mergedParent1Candidate)
        : stripUnwantedParentFields(mergedParent1Candidate);
    const mergedParent2 = stripUnwantedParentFields(
      mergeParentFields(existingParent2, newParent2, parent2Choices)
    );

    setSubmitting(true);
    setSubmitError("");
    try {
      await onSubmit({
        parent1: mergedParent1,
        parent2: mergedParent2,
        children: childrenChoices,
      });
    } catch (error) {
      setSubmitError(
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        t("applications.merge_failed", {
          defaultValue: "The application could not be merged. Check the choices and try again.",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!show) return undefined;
    setSubmitError("");
    setSubmitting(false);

    const element = document.createElement("div");
    element.setAttribute("data-lumen-overlay-root", "conflict-resolution-wizard");
    document.body.appendChild(element);
    setPortalElement(element);

    return () => {
      element.remove();
      setPortalElement(null);
    };
  }, [show]);

  useEffect(() => {
    if (!show || !portalElement) return undefined;

    previouslyFocusedElementRef.current = document.activeElement;

    const bodyChildren = Array.from(document.body.children).filter(
      (element) => element !== portalElement
    );

    const previousState = bodyChildren.map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute("aria-hidden"),
    }));

    bodyChildren.forEach((element) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusInitialElement = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute("disabled") && element.offsetParent !== null);

      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || !dialogRef.current.contains(activeElement))) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    // Capture phase prevents an underlying drawer from handling Escape or Tab first.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.cancelAnimationFrame(focusInitialElement);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;

      previousState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", ariaHidden);
        }
      });

      const previousElement = previouslyFocusedElementRef.current;
      if (previousElement instanceof HTMLElement && document.contains(previousElement)) {
        window.requestAnimationFrame(() => previousElement.focus());
      }
    };
  }, [show, portalElement, onClose]);

  if (!show || !portalElement) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm dark:bg-black/70"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-resolution-title"
        tabIndex={-1}
        className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-gray-200/70 bg-white/95 p-6 shadow-2xl shadow-indigo-900/10 outline-none dark:border-gray-700/70 dark:bg-gray-900/95 dark:text-gray-100"
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id="conflict-resolution-title"
            className="text-xl font-semibold text-gray-900 dark:text-white"
          >
            {mode === "manual-parent-merge"
              ? t("parentMerge.title", { defaultValue: "Merge parents" })
              : t("applications.resolve_conflict", "Resolve family application conflict")}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-200/70 bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-600 transition hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
          >
            {t("common.close", "Close")}
          </button>
        </div>

        <div className="mt-4 space-y-5">
          {step === 1 && (
            <ParentComparisonStep
              existingParent1={existingParent1}
              newParent1={newParent1}
              parent1Choices={parent1Choices}
              setParent1Choices={setParent1Choices}
              existingParent2={existingParent2}
              newParent2={newParent2}
              parent2Choices={parent2Choices}
              setParent2Choices={setParent2Choices}
              mode={mode}
            />
          )}

          {step === 2 && mode === "application-conflict" && (
            <ChildrenComparisonStep
              existingChildren={existingChildren}
              newChildren={newChildren}
              selectedChildren={childrenChoices}
              setSelectedChildren={setChildrenChoices}
            />
          )}

          {step === 2 && mode === "manual-parent-merge" && (
            <RelationsOverviewStep
              targetRelations={targetRelations}
              sourceRelations={sourceRelations}
            />
          )}

          {step === 3 && (
            <ReviewStep
              parent1={
                mode === "manual-parent-merge"
                  ? pickMergeableParentFields(
                      mergeParentFields(existingParent1, newParent1, parent1Choices)
                    )
                  : mergeParentFields(existingParent1, newParent1, parent1Choices)
              }
              parent1Choices={parent1Choices}
              parent2={
                existingParent2 && newParent2
                  ? mergeParentFields(existingParent2, newParent2, parent2Choices)
                  : null
              }
              parent2Choices={parent2Choices}
              children={childrenChoices}
              mode={mode}
            />
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-200/70 pt-4 dark:border-gray-700/60">
          {submitError && (
            <p role="alert" className="mr-auto text-sm font-semibold text-red-700 dark:text-red-300">
              {submitError}
            </p>
          )}
          {step > 1 ? (
            <button
              type="button"
              onClick={goToPrevStep}
              className="rounded-full border border-gray-300/80 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
            >
              {t("common.previous", "Previous")}
            </button>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-gray-300/80 bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:bg-gray-200 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
            >
              {t("common.cancel", "Cancel")}
            </button>

            {step < 3 ? (
              <button
                type="button"
                onClick={goToNextStep}
                className="rounded-full border border-indigo-200/80 bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-indigo-500/60 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:ring-offset-gray-900"
              >
                {t("common.next", "Next")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={submitting}
                className="rounded-full border border-emerald-200/80 bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:border-emerald-500/60 dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:focus-visible:ring-offset-gray-900"
              >
                {submitting
                  ? t("applications.merging", { defaultValue: "Merging..." })
                  : mode === "manual-parent-merge"
                  ? t("parentMerge.submit", { defaultValue: "Merge parents" })
                  : t("common.confirm", "Confirm")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    portalElement
  );
};

export default ParentMergeWizardModal;
